import axios from 'axios';
import { cleanJsonResponse } from '../functions/functions';
import { API_SEARCH_GAME, API_TOKEN, API_URL } from '../constantes/constantes';

/**
 * Obtiene un nuevo token de acceso desde el endpoint de AgentCore
 * 
 * @async
 * @returns {Promise<string>} Token de acceso
 * @throws {Error} Si falla la obtención del token
 */
let agentARN;
async function fetchAccessToken() {
  try {
    const response = await axios.get(API_TOKEN, {
      timeout: 10000
    });

    if (!response.data.access_token) {
      throw new Error('No access_token en respuesta');
    }

    const accessToken = response.data.access_token;
    agentARN = response.data.runtime_arn;

    return accessToken;
  } catch (error) {
    console.error('Error obteniendo token', error.message);
    throw error;
  }
}

/**
 * Obtiene un token de acceso válido
 * Siempre obtiene un token nuevo del endpoint
 * 
 * @async
 * @returns {Promise<string>} Token de acceso válido
 */
export const getValidToken = fetchAccessToken;

/**
 * Realiza una petición HTTP genérica con manejo de errores
 * 
 * @async
 * @private
 * @param {string} url - URL del endpoint
 * @param {Object} config - Configuración de axios
 * @returns {Promise<Object>} Objeto con datos y código de estado
 */
async function makeRequest(url, config) {
  try {
    const response = await axios({ url, ...config });
    return {
      data: response.data,
      statusCode: response.status
    };
  } catch (error) {
    if (error.response) {
      return { statusCode: error.response.status, data: [] };
    }

    if (error.code === 'ECONNABORTED') {
      console.error(`[Timeout] ${config.method} ${url}:`, error.message);
    } else {
      console.error(`[Network Error] ${config.method} ${url}:`, error.message, 'Code:', error.code);
    }

    return { statusCode: 500, data: [] };
  }
}

/**
 * Servicio para realizar búsquedas agenticas
 * 
 * @description Ejecuta búsquedas inteligentes usando AWS Bedrock AgentCore
 * que procesa el prompt del usuario en lenguaje natural y retorna resultados
 * contextualizados. Obtiene tokens dinámicamente desde el endpoint de AgentCore
 * si no se proporciona uno. Los tokens no se almacenan.
 * Soporta streaming para sources 'cs_v2' y 'cs_v3'.
 * 
 * @async
 * @function getSearch
 * 
 * @param {string} query - Pregunta o búsqueda del usuario en lenguaje natural
 * @param {string} source - Fuente de búsqueda (default: 'cs_v3'). Soporta streaming: 'cs_v2', 'cs_v3'
 * @param {Function} onChunk - Callback opcional para streaming (funciona con 'cs_v2' y 'cs_v3')
 * @param {string} accessToken - Token de acceso opcional. Si no se proporciona, se obtiene uno nuevo
 * @param {string} sessionId - ID de sesión opcional para mantener contexto de conversación (requerido para cs_v3)
 * 
 * @returns {Promise<Object>} Objeto con datos y código de estado
 * @returns {Promise<{data: Object|Array, statusCode: number}>} Estructura de respuesta:
 *   - data: Datos de búsqueda agentica o array vacío en caso de error
 *   - statusCode: Código HTTP de respuesta (200, 400, 500, etc.)
 * 
 * @throws {Object} Retorna objeto con statusCode 500 en caso de error de red
 * @since 1.6.0
 * @author Valeria Garcia
 * 
 * @see {@link ../constantes/constantes.js} Para configuración de AgentCore
 * @see {@link ../components/BuscadorApp.js} Componente que consume este servicio
 */
export async function getSearch(query, source = 'cs_v3', onChunk = null, accessToken = null, sessionId = null) {
  // Validación de parámetros
  if (!query || typeof query !== 'string') {
    return { statusCode: 400, data: [] };
  }

  try {
    // Obtener token si no fue proporcionado
    if (!accessToken) {
      accessToken = await getValidToken();
    }

    // Construcción de la URL de AgentCore
    const AGENT_CORE_URL = 'https://bedrock-agentcore.us-east-1.amazonaws.com';
    const encodedArn = encodeURIComponent(agentARN);
    const url = `${AGENT_CORE_URL}/runtimes/${encodedArn}/invocations?qualifier=DEFAULT`;

    // Construcción del body con los parámetros requeridos
    const requestData = {
      prompt: query.trim(),
      source: source
    };

    // Incluir session_id si fue proporcionado (requerido para cs_v3)
    // if (sessionId) {
    //   requestData.session_id = sessionId;
    // }

    // Si source es 'utv_v3' y hay callback, usar streaming
    if ((source === 'cs_v4') && typeof onChunk === 'function') {
      return await getSearchStreaming(url, accessToken, requestData, onChunk);
    }

    // Modo no-streaming (comportamiento original)
    const response = await axios({
      method: 'POST',
      url: url,
      timeout: 600000,
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json; charset=utf-8'
      },
      data: requestData
    });

    // Solo parsear a JSON si source es 'generate_query'
    let parsedData;
    if (source === 'generate_query_v2') {
      parsedData = cleanJsonResponse(response.data);

      if (parsedData) {
        console.log('[getSearch] ✓ Respuesta parseada como JSON exitosamente');
      } else {
        console.warn('[getSearch] ✗ Error al parsear respuesta como JSON');
        return { statusCode: 400, data: [] };
      }
    } else {
      parsedData = response.data;
      console.log('[getSearch] Respuesta sin parseo (source !== generate_query)');
    }

    return {
      data: parsedData,
      statusCode: 200
    };

  } catch (error) {
    // Manejo de errores de red/timeout
    if (error.response) {
      console.error('[getSearch] AgentCore error:', error.response.status, error.response.data);
      return { statusCode: error.response.status, data: [] };
    }

    // Error de red o timeout
    if (error.code === 'ECONNABORTED') {
      console.error('[getSearch] Timeout después de 10 minutos:', error.message);
    } else {
      console.error('[getSearch] Network error:', error.message, 'Code:', error.code);
    }

    return { statusCode: 500, data: [] };
  }
}

/**
 * Realiza una búsqueda con streaming
 * 
 * @async
 * @private
 * @param {string} url - URL del endpoint
 * @param {string} accessToken - Token de acceso
 * @param {Object} requestData - Datos de la petición
 * @param {Function} onChunk - Callback para procesar cada chunk
 * 
 * @returns {Promise<Object>} Objeto con datos y código de estado
 */
async function getSearchStreaming(url, accessToken, requestData, onChunk) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'text/event-stream',
        'Content-Type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      console.error('[getSearchStreaming] Error HTTP:', response.status);
      return { statusCode: response.status, data: [] };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';
    let chunkCount = 0;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Procesar último chunk si hay datos en buffer
        if (buffer.trim()) {
          try {
            const chunkData = parseSSELine(buffer);
            if (chunkData) {
              onChunk(chunkData);
              fullContent += chunkData;
              chunkCount++;
            }
          } catch (e) {
            console.warn('[getSearchStreaming] Error procesando último chunk:', e);
          }
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Procesar líneas completas (SSE usa \n como separador)
      const lines = buffer.split('\n');
      buffer = lines[lines.length - 1]; // Guardar línea incompleta

      for (let i = 0; i < lines.length - 1; i++) {
        const line = lines[i].trim();
        if (line) {
          try {
            const chunkData = parseSSELine(line);
            if (chunkData) {
              onChunk(chunkData);
              fullContent += chunkData;
              chunkCount++;
            }
          } catch (e) {
            console.warn('[getSearchStreaming] Error procesando chunk:', e, 'Línea:', line.substring(0, 100));
          }
        }
      }
    }

    return {
      data: { resume: fullContent },
      statusCode: 200
    };

  } catch (error) {
    console.error('[getSearchStreaming] Error:', error.message);
    return { statusCode: 500, data: [] };
  }
}

/**
 * Parsea una línea en formato Server-Sent Events (SSE)
 * Formato: data: "texto plano"
 * 
 * @private
 * @param {string} line - Línea a procesar
 * @returns {string|null} Contenido extraído o null si no es válido
 */
function parseSSELine(line) {
  try {
    // Remover prefijo "data: " si existe
    let content = line;
    if (line.startsWith('data:')) {
      content = line.substring(5).trim();
    }

    // Si el contenido está entre comillas, removerlas
    if (content.startsWith('"') && content.endsWith('"')) {
      content = content.slice(1, -1);
    }

    // Decodificar caracteres escapados Unicode (ej: \u00e9 -> é)
    content = JSON.parse('"' + content + '"');

    if (content && typeof content === 'string' && content.length > 0) {
      return content;
    }

    return null;
  } catch (e) {
    console.warn('[parseSSELine] Error parseando línea:', e.message, 'Línea:', line.substring(0, 100));
    return null;
  }
}

/**
 * Envía los resultados de generate_query a la API de búsqueda v2
 * 
 * @description Realiza una petición POST a la API de búsqueda v2 con los datos
 * parseados de la respuesta de generate_query. Esta API procesa los resultados
 * generados y retorna datos adicionales o procesados.
 * 
 * @async
 * @function searchV2
 * 
 * @param {Object} generateQueryData - Datos parseados de la respuesta de generate_query
 * @param {string} generateQueryData.resume - Resumen de la búsqueda
 * @param {Array} generateQueryData.notes - Notas/resultados de la búsqueda
 * 
 * @returns {Promise<Object>} Objeto con datos y código de estado
 * @returns {Promise<{data: Object|Array, statusCode: number}>} Estructura de respuesta
 * 
 * @throws {Object} Retorna objeto con statusCode 500 en caso de error de red
 * 
 * @since 1.7.0
 */
export async function searchV2(generateQueryData) {
  if (!generateQueryData || typeof generateQueryData !== 'object') {
    return { statusCode: 400, data: [] };
  }

  const url = API_URL + 'search_v3';
  return makeRequest(url, {
    method: 'POST',
    timeout: 600000,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json; charset=utf-8'
    },
    data: generateQueryData
  });
}

/**
 * Envía los resultados de search_games a la API de búsqueda de juegos
 * 
 * @description Realiza una petición POST a la API de búsqueda de juegos con los datos
 * parseados de la respuesta de search_games. Esta API procesa los resultados
 * de juegos y retorna datos adicionales o procesados.
 * 
 * @async
 * @function searchGames
 * 
 * @param {Object} gamesData - Datos parseados de la respuesta de search_games
 * @param {number} gamesData.total_results - Total de resultados
 * @param {string} gamesData.query_summary - Resumen de la búsqueda
 * @param {Array} gamesData.games - Array de juegos encontrados
 * 
 * @returns {Promise<Object>} Objeto con datos y código de estado
 * @returns {Promise<{data: Object|Array, statusCode: number}>} Estructura de respuesta
 * 
 * @throws {Object} Retorna objeto con statusCode 500 en caso de error de red
 * 
 * @since 1.8.0
 */
export async function searchGames(gamesData) {
  if (!gamesData || typeof gamesData !== 'object') {
    return { statusCode: 400, data: [] };
  }

  const url = API_SEARCH_GAME + 'event-by-query';
  return makeRequest(url, {
    method: 'POST',
    timeout: 600000,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json; charset=utf-8'
    },
    data: gamesData
  });
}